import { Box, Button } from '@mui/material';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FieldConfiguration } from '../api/ConfigApi';
import { handleUnauthorizedResponse } from '../api/http';
import InvoiceApi from '../api/InvoiceApi';
import { AuthContext, AuthData } from '../context/auth';
import { ConfigContext, ConfigData } from '../context/config';
import { PolicyContext, PolicyData } from '../context/policy';
import { CurrencyCode } from '../types/currencyCode';
import { Payment } from '../types/payments';
import {
	FieldValues,
	FieldValuesMap,
	PaymentCreateRequest,
	PolicyInvoice2Response,
} from '../types/policy';
import { MUIVariant } from '../util/palette';
import { formatCurrency } from '../util/util';
import DefaultDialog from './DefaultDialog';
import DefaultDatePicker from './form/DefaultDatePicker';
import LargeCenterSpinner from './LargeCenterSpinner';
import Field from './policy/fields/Field';
import { errorToast } from './Toast';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	policyData: PolicyData;
	invoiceLocator: string;
	isPaid: boolean;
	onPayment: (
		policyId: string,
		locator: string,
		req: PaymentCreateRequest,
	) => void;
	currency: CurrencyCode;
}

const PayInvoiceModal = (props: Props) => {
	const { authData, configData, invoiceLocator, isPaid, onPayment, currency } =
		props;
	const {
		paymentConfiguration: { paymentConfiguration },
	} = configData;
	const { fields } = paymentConfiguration;

	const [isOpen, setIsOpen] = useState(false);
	const [fieldValues, setFieldValues] = useState({} as FieldValuesMap);
	const [invoice, setInvoice] = useState<PolicyInvoice2Response>(null);

	useEffect(() => {
		const source = axios.CancelToken.source();

		const fetchInvoice = async () => {
			try {
				const invoiceResp = await InvoiceApi.fetchInvoice(
					authData.auth.authorizationToken,
					invoiceLocator,
					{ cancelToken: source.token },
				);
				setInvoice(invoiceResp.data);
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
					errorToast('Failed to get invoice data');
				}
			}
		};

		fetchInvoice();

		return () => {
			source.cancel();
		};
	}, [invoiceLocator]);

	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);

	const payInvoice = async () => {
		const req = { fieldValues };
		onPayment(invoice.policyLocator, invoice.locator, req);
		close();
	};

	const updateField = (field: FieldConfiguration, value: FieldValues) => {
		fieldValues[field.name] = value;
		setFieldValues(fieldValues);
	};

	const renderPayments = () => {
		const { payments } = invoice;
		return payments.map((p: Payment) => {
			return (
				<div key={p.displayId}>
					<div>Payment #{p.displayId}</div>
					<DefaultDatePicker
						value={new Date(p.postedTimestamp)}
						onChange={null}
						label='Payment Date'
						style={{ width: '48%', marginTop: '16px', marginRight: '4%' }}
						disabled
						showCalendar
						id='payment-date'
					/>
					<TextField
						id='payment-amount'
						style={{ width: '48%', marginTop: '16px' }}
						label='Payment Amount'
						value={formatCurrency(parseFloat(p.amount), currency)}
						disabled
					/>
					{renderFields(fields, p.fieldValues)}
				</div>
			);
		});
	};

	const renderPaymentDetails = () => {
		return (
			<div>
				<DefaultDatePicker
					value={invoice ? new Date(parseInt(invoice.dueTimestamp, 10)) : null}
					onChange={null}
					label='Payment Due Date'
					style={{ width: '48%', marginTop: '16px', marginRight: '4%' }}
					disabled
					showCalendar
					id='payment-due-date'
				/>
				<TextField
					id='toal-due'
					style={{ width: '48%', marginTop: '16px' }}
					label='Total Due'
					value={
						invoice
							? formatCurrency(parseFloat(invoice.totalDue), currency)
							: null
					}
					disabled
				/>
				{renderFields(fields, fieldValues)}
			</div>
		);
	};

	const renderFields = (
		fieldConfigs: FieldConfiguration[],
		fieldValuesMap: FieldValuesMap,
	) => {
		return fieldConfigs.map((f) => {
			const value = fieldValuesMap[f.name] || [''];
			return (
				<Box key={f.name} sx={{ mt: 2 }}>
					<Field
						key={f.name}
						field={f}
						value={value}
						isDisabled={isPaid}
						allValues={null}
						updateOnChange
						updateFieldValue={updateField}
					/>
				</Box>
			);
		});
	};

	const heading = 'Record a payment';
	const actions = [
		{ text: 'Record Payment', onClick: payInvoice },
		{ text: 'Cancel', onClick: close, variant: 'text' as MUIVariant },
	];

	const renderContent = () => {
		if (!invoice) {
			return <LargeCenterSpinner />;
		} else if (isPaid) {
			return renderPayments();
		} else {
			return renderPaymentDetails();
		}
	};

	return (
		<>
			<Button
				variant='outlined'
				color='primary'
				onClick={open}
				data-testid='PayInvoiceModal__Button--Open'
			>
				Record Payment
			</Button>
			<DefaultDialog
				actions={actions}
				open={isOpen}
				onClose={close}
				title={heading}
			>
				{renderContent()}
			</DefaultDialog>
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData' | 'policyData'>) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<PolicyContext.Consumer>
							{(policyData) => (
								<Component
									{...props}
									configData={configData}
									authData={authData}
									policyData={policyData}
								/>
							)}
						</PolicyContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(PayInvoiceModal);
